import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { matrimonyApi } from '../matrimonyApi';

function Field({ label, value, icon }) {
  if (!value && value !== 0) return null;
  return (
    <div className="m-detail-field">
      <div className="m-detail-field-label">{icon && `${icon} `}{label}</div>
      <div className="m-detail-field-value">{value}</div>
    </div>
  );
}

function Hobbies({ text }) {
  if (!text) return null;
  const items = text.split(',').map(h => h.trim()).filter(Boolean);
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
      {items.map((h, i) => <span key={i} className="m-tag">{h}</span>)}
    </div>
  );
}

function initials(name) {
  return (name || 'AB').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

export default function MatrimonyProfileDetail() {
  const { id } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [activeTab, setActiveTab] = useState('about');
  const [zoomPhoto, setZoomPhoto] = useState(false);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') setZoomPhoto(false);
  }, []);

  useEffect(() => {
    if (zoomPhoto) document.addEventListener('keydown', handleKeyDown);
    else document.removeEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [zoomPhoto, handleKeyDown]);

  useEffect(() => {
    setLoading(true);
    matrimonyApi.getProfile(id)
      .then(setProfile)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div className="m-container">
      <div className="m-loading">
        <div className="m-spinner" />
        <span>Loading profile…</span>
      </div>
    </div>
  );

  if (error || !profile) return (
    <div className="m-container">
      <div className="m-empty-state">
        <span className="m-empty-state-icon">😔</span>
        <h3>Profile Not Found</h3>
        <p>This profile may not be available or has been removed.</p>
        <Link to="/profiles" className="m-btn m-btn-primary">← Back to Profiles</Link>
      </div>
    </div>
  );

  const p = profile;
  const locationStr = [p.city, p.state, p.country].filter(Boolean).join(', ');

  return (
    <div className="m-container" style={{ maxWidth: 900 }}>
      {/* Full-photo lightbox */}
      {zoomPhoto && p.profile_photo && (
        <div className="m-photo-overlay" onClick={() => setZoomPhoto(false)}>
          <img src={p.profile_photo} alt={p.full_name} className="m-photo-overlay-img" />
          <button className="m-photo-overlay-close" onClick={() => setZoomPhoto(false)}>✕</button>
        </div>
      )}

      <Link to="/profiles" className="m-btn m-btn-ghost m-btn-sm" style={{ marginBottom: 20, display: 'inline-flex' }}>
        ← Back to Profiles
      </Link>

      {/* Header */}
      <div className="m-detail-header">
        <div
          className={`m-detail-avatar${p.profile_photo ? ' m-avatar-zoomable' : ''}`}
          onClick={() => p.profile_photo && setZoomPhoto(true)}
          title={p.profile_photo ? 'Click to view full photo' : undefined}
        >
          {p.profile_photo
            ? <img src={p.profile_photo} alt={p.full_name} />
            : initials(p.full_name)}
        </div>
        <div className="m-detail-info">
          <h1 className="m-detail-name">{p.full_name || 'Anonymous'}</h1>
          <div className="m-detail-meta">
            {p.age && (
              <span className="m-badge m-badge-muted">🎂 {p.age} years old</span>
            )}
            {locationStr && (
              <span className="m-badge m-badge-muted">📍 {locationStr}</span>
            )}
            {p.gender && (
              <span className={`m-badge m-badge-${p.gender}`}>
                {p.gender === 'bride' ? '👰 Bride' : '🤵 Groom'}
              </span>
            )}
            {p.religion && <span className="m-badge m-badge-gold">🕌 {p.religion}</span>}
          </div>
          {p.about_me && (
            <p style={{ color: 'var(--m-muted)', fontSize: 14, lineHeight: 1.7, maxWidth: 500 }}>
              {p.about_me}
            </p>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="m-tabs">
        {[
          { key: 'about',    label: '👤 About' },
          { key: 'family',   label: '👨‍👩‍👧‍👦 Family' },
          { key: 'career',   label: '💼 Education & Career' },
          { key: 'prefs',    label: '💍 Partner Preferences' },
        ].map(t => (
          <button
            key={t.key}
            className={`m-tab${activeTab === t.key ? ' active' : ''}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* About */}
      {activeTab === 'about' && (
        <div className="m-detail-section">
          <div className="m-detail-section-title">👤 Personal Information</div>
          <div className="m-detail-grid">
            <Field label="Height" value={p.height_cm ? `${p.height_cm} cm` : null} icon="📏" />
            <Field label="Weight" value={p.weight_kg ? `${p.weight_kg} kg` : null} icon="⚖️" />
            <Field label="Complexion" value={p.complexion} icon="🌟" />
            <Field label="Blood Group" value={p.blood_group} icon="🩸" />
            <Field label="Religion" value={p.religion} icon="🙏" />
            <Field label="Caste" value={p.caste} icon="🏷️" />
            <Field label="Sub Caste" value={p.sub_caste} icon="🏷️" />
            <Field label="Mother Tongue" value={p.mother_tongue} icon="🗣️" />
            <Field label="Diet" value={p.diet} icon="🍽️" />
            <Field label="Smoking" value={p.smoking} icon="🚬" />
            <Field label="Drinking" value={p.drinking} icon="🍷" />
          </div>

          {p.horoscope_url && (
            <div style={{ marginTop: 16 }}>
              <a
                href={p.horoscope_url}
                target="_blank"
                rel="noopener noreferrer"
                className="m-btn m-btn-outline m-btn-sm"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                🔯 View Horoscope
              </a>
            </div>
          )}

          {p.hobbies && (
            <div style={{ marginTop: 20 }}>
              <div className="m-detail-field-label">🎨 HOBBIES & INTERESTS</div>
              <Hobbies text={p.hobbies} />
            </div>
          )}
        </div>
      )}

      {/* Family */}
      {activeTab === 'family' && (
        <div className="m-detail-section">
          <div className="m-detail-section-title">👨‍👩‍👧‍👦 Family Background</div>
          <div className="m-detail-grid">
            <Field label="Family Type"        value={p.family_type}       icon="🏠" />
            <Field label="Family Values"      value={p.family_values}     icon="💝" />
            <Field label="Father's Occupation" value={p.father_occupation} icon="👔" />
            <Field label="Mother's Occupation" value={p.mother_occupation} icon="👩‍💼" />
            <Field label="No. of Siblings"    value={p.siblings != null ? String(p.siblings) : null} icon="👥" />
          </div>
        </div>
      )}

      {/* Education & Career */}
      {activeTab === 'career' && (
        <div className="m-detail-section">
          <div className="m-detail-section-title">📚 Education &amp; Career</div>
          <div className="m-detail-grid">
            <Field label="Education"     value={p.education}       icon="🎓" />
            <Field label="Field"         value={p.education_field} icon="📖" />
            <Field label="Occupation"    value={p.occupation}      icon="💼" />
            <Field label="Employer"      value={p.employer}        icon="🏢" />
            <Field label="Annual Income" value={p.annual_income}   icon="💰" />
          </div>
        </div>
      )}

      {/* Partner preferences */}
      {activeTab === 'prefs' && (
        <div className="m-detail-section">
          <div className="m-detail-section-title">💍 Partner Preferences</div>
          <div className="m-detail-grid">
            {(p.pref_age_min || p.pref_age_max) && (
              <Field
                label="Age Range"
                value={`${p.pref_age_min || '?'} – ${p.pref_age_max || '?'} years`}
                icon="🎂"
              />
            )}
            {(p.pref_height_min || p.pref_height_max) && (
              <Field
                label="Height Range"
                value={`${p.pref_height_min || '?'} – ${p.pref_height_max || '?'} cm`}
                icon="📏"
              />
            )}
            <Field label="Preferred Religion"  value={p.pref_religion}  icon="🙏" />
            <Field label="Preferred Caste"     value={p.pref_caste}     icon="🏷️" />
            <Field label="Preferred Education" value={p.pref_education} icon="🎓" />
            <Field label="Preferred Income"    value={p.pref_income}    icon="💰" />
            <Field label="Preferred Location"  value={p.pref_location}  icon="📍" />
          </div>
        </div>
      )}
    </div>
  );
}
